import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Activity, Target, TrendingUp, Users } from 'lucide-react';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import MetricsCard from './components/MetricsCard';
import PerformanceChart from './components/PerformanceChart';
import TopPerformersTable from './components/TopPerformersTable';
import {
  getSummaryStatistics,
  getInteractionTimeSeries,
  getTopPerformers
} from '../../services/recommendationAnalyticsService';

const RecommendationPerformanceAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryStats, setSummaryStats] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [topSongs, setTopSongs] = useState([]);
  const [topRoles, setTopRoles] = useState([]);
  const [topMusicals, setTopMusicals] = useState([]);
  const [timeRange, setTimeRange] = useState(30);

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all analytics data in parallel
      const [stats, timeSeries, songs, roles, musicals] = await Promise.all([
        getSummaryStatistics(),
        getInteractionTimeSeries(timeRange),
        getTopPerformers('song', 10),
        getTopPerformers('role', 10),
        getTopPerformers('musical', 10)
      ]);

      setSummaryStats(stats);
      setTimeSeriesData(timeSeries);
      setTopSongs(songs);
      setTopRoles(roles);
      setTopMusicals(musicals);
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError(err?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <h3 className="text-red-800 font-semibold mb-2">Error Loading Analytics</h3>
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchAnalyticsData}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Recommendation Performance Analytics | RoleCall</title>
        <meta 
          name="description" 
          content="AI recommendation system performance metrics and analytics dashboard" 
        />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />
        
        <div className="container mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-white mb-2">
              Recommendation Performance Analytics
            </h1>
            <p className="text-purple-200 text-lg">
              Track AI recommendation effectiveness and user engagement metrics
            </p>
          </div>

          {/* Time Range Filter */}
          <div className="mb-6 flex items-center gap-4">
            <span className="text-white font-medium">Time Range:</span>
            <div className="flex gap-2">
              {[7, 30, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => setTimeRange(days)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    timeRange === days
                      ? 'bg-white text-purple-900 shadow-lg'
                      : 'bg-purple-800/50 text-white hover:bg-purple-700/70'
                  }`}
                >
                  {days} Days
                </button>
              ))}
            </div>
          </div>

          {/* Summary Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <MetricsCard
              title="Total Impressions"
              value={summaryStats?.totalImpressions || 0}
              icon={Users}
              color="purple"
              trend={0}
            />
            <MetricsCard
              title="Average CTR"
              value={summaryStats?.avgClickThroughRate || 0}
              suffix="%"
              icon={Target}
              color="blue"
              trend={0}
            />
            <MetricsCard
              title="Avg Completion Rate"
              value={summaryStats?.avgCompletionRate || 0}
              suffix="%"
              icon={TrendingUp}
              color="green"
              trend={0}
            />
            <MetricsCard
              title="Avg Confidence Score"
              value={(summaryStats?.avgConfidence || 0) * 100}
              suffix="%"
              icon={Activity}
              color="orange"
              trend={0}
            />
          </div>

          {/* Performance Over Time Chart */}
          <div className="mb-8">
            <PerformanceChart 
              data={timeSeriesData} 
              title="Recommendation Interactions Over Time"
            />
          </div>

          {/* Top Performers by Content Type */}
          <div className="space-y-8">
            <TopPerformersTable
              data={topSongs}
              type="song"
              title="Top Performing Song Recommendations"
            />
            
            <TopPerformersTable
              data={topRoles}
              type="role"
              title="Top Performing Role Recommendations"
            />
            
            <TopPerformersTable
              data={topMusicals}
              type="musical"
              title="Top Performing Musical Recommendations"
            />
          </div>

          {/* Additional Stats Section */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement Breakdown</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Clicks</span>
                  <span className="font-semibold text-purple-600">{summaryStats?.totalClicks || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Completions</span>
                  <span className="font-semibold text-green-600">{summaryStats?.totalCompletions || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Favorites</span>
                  <span className="font-semibold text-orange-600">{summaryStats?.totalFavorites || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Dismissals</span>
                  <span className="font-semibold text-red-600">{summaryStats?.totalDismissals || 0}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Rates</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-600">Click-Through Rate</span>
                    <span className="text-sm font-semibold">{summaryStats?.avgClickThroughRate?.toFixed(1) || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full" 
                      style={{ width: `${summaryStats?.avgClickThroughRate || 0}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-600">Completion Rate</span>
                    <span className="text-sm font-semibold">{summaryStats?.avgCompletionRate?.toFixed(1) || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${summaryStats?.avgCompletionRate || 0}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-600">Favorite Rate</span>
                    <span className="text-sm font-semibold">{summaryStats?.avgFavoriteRate?.toFixed(1) || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-orange-600 h-2 rounded-full" 
                      style={{ width: `${summaryStats?.avgFavoriteRate || 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Confidence</h3>
              <div className="flex flex-col items-center justify-center h-full">
                <div className="relative w-32 h-32">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="#e5e7eb"
                      strokeWidth="12"
                      fill="none"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="#6e32b9"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 56}`}
                      strokeDashoffset={`${2 * Math.PI * 56 * (1 - (summaryStats?.avgConfidence || 0))}`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold text-gray-900">
                      {((summaryStats?.avgConfidence || 0) * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
                <p className="mt-4 text-sm text-gray-600 text-center">
                  Average AI recommendation confidence score
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RecommendationPerformanceAnalyticsDashboard;